import { useEffect } from 'react'
import { COMMAND_BAR_SHORTCUT_MAP } from '../commandBarActions'
import type { CommandBarActionId, CommandBarItem } from '../types'

type UseCommandBarOverlayKeyboardArgs = {
  open: boolean
  flatItems: CommandBarItem[]
  onClose: () => void
  onMoveSelection: (direction: 'up' | 'down') => void
  onExecuteSelected: () => void
  onExecuteAction: (actionId: CommandBarActionId) => void
  /** Faux si l'écran n'a pas le geste de l'action : la lettre va alors au champ de recherche. */
  canExecuteAction: (actionId: CommandBarActionId) => boolean
}

/** Letter from KeyboardEvent.code (KeyA → a), reliable with Shift. */
function getLetterFromKeyboardEvent(event: KeyboardEvent): string | null {
  if (event.code.startsWith('Key') && event.code.length === 4) {
    return event.code.slice(3).toLowerCase()
  }
  if (/^[a-z]$/i.test(event.key)) {
    return event.key.toLowerCase()
  }
  return null
}

function hasQuickFireModifier(event: KeyboardEvent): boolean {
  // Shift alone — avoids Chrome/OS ⌘/Ctrl shortcuts and leaves plain letters for search.
  return event.shiftKey && !event.metaKey && !event.ctrlKey && !event.altKey
}

export function useCommandBarOverlayKeyboard({
  open,
  flatItems,
  onClose,
  onMoveSelection,
  onExecuteSelected,
  onExecuteAction,
  canExecuteAction,
}: UseCommandBarOverlayKeyboardArgs) {
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          event.preventDefault()
          onClose()
          return
        case 'ArrowUp':
          if (flatItems.length === 0) return
          event.preventDefault()
          onMoveSelection('up')
          return
        case 'ArrowDown':
          if (flatItems.length === 0) return
          event.preventDefault()
          onMoveSelection('down')
          return
        case 'Enter':
          event.preventDefault()
          onExecuteSelected()
          return
        default:
          break
      }

      // Quick-fire: Shift + letter (does not require the action to be visible).
      if (!hasQuickFireModifier(event)) return

      const letter = getLetterFromKeyboardEvent(event)
      if (!letter) return

      const actionId = COMMAND_BAR_SHORTCUT_MAP[letter]
      if (!actionId) return

      const visibleItem = flatItems.find(
        (candidate) => candidate.actionId === actionId
      )
      if (visibleItem?.disabled || !canExecuteAction(actionId)) return

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      onExecuteAction(actionId)
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [
    canExecuteAction,
    flatItems,
    onClose,
    onExecuteAction,
    onExecuteSelected,
    onMoveSelection,
    open,
  ])
}
